import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { ToastService } from '../../../../core/services/toast.service';
import * as WarehouseActions from './warehouse.actions';
import { catchError, map, mergeMap, tap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { APP_PATHS } from '../../../../core/constants/platform-config.constants';

import { TranslationService } from '../../../../services/translation';
import { WarehouseService } from '../../../../core/services/gse/warehouse.service';

@Injectable()
export class WarehouseEffects {
    private actions$ = inject(Actions);
    private warehouseService = inject(WarehouseService);
    private router = inject(Router);
    private toastService = inject(ToastService);
    private translationService = inject(TranslationService);

    loadWarehouses$ = createEffect(() =>
        this.actions$.pipe(
        ofType(WarehouseActions.loadWarehouses),
        mergeMap(() =>
            this.warehouseService.getAllWarehouses().pipe(
                map((response) => WarehouseActions.loadWarehousesSuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.loadWarehousesFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to load warehouses',
                        })
                    )
                )
            )
        )
        )
    );

    loadActiveWarehouses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.loadActiveWarehouses),
            mergeMap(() =>
                this.warehouseService.getAllWarehouses({ status: 'ACTIVE' }).pipe(
                    map((response) =>
                        WarehouseActions.loadActiveWarehousesSuccess({ response: response.body })
                    ),
                    catchError((error) =>
                        of(
                            WarehouseActions.loadActiveWarehousesFailure({
                                error:
                                    error?.error?.message ||
                                    error?.message ||
                                    'Failed to load active warehouses',
                            })
                        )
                    )
                )
            )
        )
    );

    loadWarehouse$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.loadWarehouse),
            mergeMap(({ id }) =>
                this.warehouseService.getWarehouse(id).pipe(
                map((response) => WarehouseActions.loadWarehouseSuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.loadWarehouseFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to load warehouse details',
                        })
                    )
                )
                )
            )
        )
    );

    createWarehouse$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.createWarehouse),
            mergeMap(({ request }) =>
                this.warehouseService.createWarehouse(request).pipe(
                map((response) => WarehouseActions.createWarehouseSuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.createWarehouseFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to create warehouse',
                        })
                    )
                )
                )
            )
        )
    );

    createWarehouseSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(WarehouseActions.createWarehouseSuccess),
                tap((action) => {
                    this.toastService.showSuccess(
                        this.translationService.translate('common.success'),
                        action.response.message || 'Warehouse created successfully'
                    );
                })
            ),
        { dispatch: false }
    );

    updateWarehouse$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.updateWarehouse),
            mergeMap(({ id, request }) =>
                this.warehouseService.updateWarehouse(id, request).pipe(
                map((response) => WarehouseActions.updateWarehouseSuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.updateWarehouseFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to update warehouse',
                        })
                    )
                )
                )
            )
        )
    );

    updateWarehouseSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(WarehouseActions.updateWarehouseSuccess),
                tap((action) => {
                    this.toastService.showSuccess(
                        this.translationService.translate('common.success'),
                        action.response.message || 'Warehouse updated successfully'
                    );
                })
            ),
        { dispatch: false }
    );

    // Zone Effects
    createZone$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.createZone),
            mergeMap(({ warehouseId, request }) =>
                this.warehouseService.createZone(warehouseId, request).pipe(
                map((response) => WarehouseActions.createZoneSuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.createZoneFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to create zone',
                        })
                    )
                )
                )
            )
        )
    );

    createZoneSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(WarehouseActions.createZoneSuccess),
                tap((action) => {
                    this.toastService.showSuccess(
                        this.translationService.translate('common.success'),
                        action.response.message || 'Zone created successfully'
                    );
                })
            ),
        { dispatch: false }
    );

    updateZone$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.updateZone),
            mergeMap(({ warehouseId, zoneId, request }) =>
                this.warehouseService.updateZone(warehouseId, zoneId, request).pipe(
                map((response) => WarehouseActions.updateZoneSuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.updateZoneFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to update zone',
                        })
                    )
                )
                )
            )
        )
    );

    updateZoneSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(WarehouseActions.updateZoneSuccess),
                tap((action) => {
                    this.toastService.showSuccess(
                        this.translationService.translate('common.success'),
                        action.response.message || 'Zone updated successfully'
                    );
                })
            ),
        { dispatch: false }
    );

    deleteZone$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.deleteZone),
            mergeMap(({ warehouseId, zoneId }) =>
                this.warehouseService.deleteZone(warehouseId, zoneId).pipe(
                map(() =>
                    WarehouseActions.deleteZoneSuccess({
                        warehouseId,
                        zoneId,
                        message: undefined,
                    })
                ),
                catchError((error) =>
                    of(
                        WarehouseActions.deleteZoneFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to delete zone',
                        })
                    )
                )
                )
            )
        )
    );

    deleteZoneSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(WarehouseActions.deleteZoneSuccess),
                tap((action) => {
                    this.toastService.showSuccess(
                        this.translationService.translate('common.success'),
                        action.message || 'Zone deleted successfully'
                    );
                })
            ),
        { dispatch: false }
    );

    // Lookups
    loadLookups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.loadLookups),
            mergeMap(() =>
                this.warehouseService.getWarehouseLookups().pipe(
                map((response) => WarehouseActions.loadLookupsSuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.loadLookupsFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to load warehouse lookups',
                        })
                    )
                )
                )
            )
        )
    );

    // Dashboard Summary
    loadDashboardSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.loadDashboardSummary),
            mergeMap(() =>
                this.warehouseService.getWarehouseDashboardSummary().pipe(
                map((response) => WarehouseActions.loadDashboardSummarySuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.loadDashboardSummaryFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to load warehouse dashboard summary',
                        })
                    )
                )
                )
            )
        )
    );

    // Zone Type Distribution
    loadZoneTypeDistribution$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.loadZoneTypeDistribution),
            mergeMap(() =>
                this.warehouseService.getZoneTypeDistribution().pipe(
                map((response) => WarehouseActions.loadZoneTypeDistributionSuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.loadZoneTypeDistributionFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to load zone type distribution',
                        })
                    )
                )
                )
            )
        )
    );

    // Warehouses By Zone Count
    loadWarehousesByZoneCount$ = createEffect(() =>
        this.actions$.pipe(
            ofType(WarehouseActions.loadWarehousesByZoneCount),
            mergeMap(() =>
                this.warehouseService.getWarehousesByZoneCount().pipe(
                map((response) => WarehouseActions.loadWarehousesByZoneCountSuccess({ response: response.body })),
                catchError((error) =>
                    of(
                        WarehouseActions.loadWarehousesByZoneCountFailure({
                            error:
                                error?.error?.message ||
                                error?.message ||
                                'Failed to load warehouses by zone count',
                        })
                    )
                )
                )
            )
        )
    );

    refreshWarehouseData$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                WarehouseActions.createWarehouseSuccess,
                WarehouseActions.updateWarehouseSuccess,
                WarehouseActions.createZoneSuccess,
                WarehouseActions.updateZoneSuccess,
                WarehouseActions.deleteZoneSuccess
            ),
            switchMap(() => [
                WarehouseActions.loadWarehouses(),
                WarehouseActions.loadActiveWarehouses(),
                WarehouseActions.loadDashboardSummary(),
                WarehouseActions.loadZoneTypeDistribution(),
                WarehouseActions.loadWarehousesByZoneCount()
            ])
        )
    );

    // Error Handling
    failures$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    WarehouseActions.loadWarehousesFailure,
                    WarehouseActions.loadActiveWarehousesFailure,
                    WarehouseActions.loadWarehouseFailure,
                    WarehouseActions.createWarehouseFailure,
                    WarehouseActions.updateWarehouseFailure,
                    WarehouseActions.createZoneFailure,
                    WarehouseActions.updateZoneFailure,
                    WarehouseActions.deleteZoneFailure,
                    WarehouseActions.loadLookupsFailure,
                    WarehouseActions.loadDashboardSummaryFailure,
                    WarehouseActions.loadZoneTypeDistributionFailure,
                    WarehouseActions.loadWarehousesByZoneCountFailure
                ),
                tap(({ error }) => {
                    const apiResponseMessage = error;
                    const translated = this.translationService.translate(apiResponseMessage);
                    const displayMessage =
                        translated !== apiResponseMessage
                            ? translated
                            : apiResponseMessage || this.translationService.translate('common.unexpectedError');

                    this.toastService.showError(
                        this.translationService.translate('common.error'),
                        displayMessage
                    );
                })
            ),
        { dispatch: false }
    );

}
